import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("polling");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/shop/orders/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") { setState("paid"); clear(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      if (++tries.current >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pt-40 pb-32 max-w-2xl mx-auto px-4 text-center" data-testid="order-success-page">
      <Seo title="Commande | Boutique Horsepower" />
      {state === "polling" && (<><Clock className="mx-auto text-hp-yellow animate-pulse" size={48} /><h1 className="font-display font-black uppercase tracking-tight text-3xl mt-6">Vérification du paiement…</h1><p className="text-neutral-400 mt-3">Quelques secondes, nous confirmons votre commande.</p></>)}
      {state === "paid" && order && (
        <>
          <CheckCircle2 className="mx-auto text-hp-yellow" size={56} />
          <h1 className="font-display font-black uppercase tracking-tight text-4xl mt-6">Merci, commande confirmée !</h1>
          <p className="text-neutral-300 mt-3">Commande <span className="text-hp-yellow font-bold" data-testid="order-id">#{order.order_id}</span> · {eur(order.total)}<br />Un e-mail de confirmation a été envoyé à {order.email}.</p>
          <p className="text-neutral-400 mt-4 text-sm">{order.delivery === "pickup" ? `Retrait dans votre centre : ${order.centre_name}. Nous vous prévenons dès qu'elle est prête.` : "Votre commande sera expédiée sous quelques jours."}</p>
          <ul className="hp-card mt-8 p-5 text-left text-sm divide-y divide-white/5">
            {order.items.map((i, idx) => <li key={idx} className="py-2 flex justify-between"><span>{i.name} <span className="text-neutral-500">{Object.values(i.options).join(" · ")}</span> × {i.qty}</span><span>{eur(i.unit_price * i.qty)}</span></li>)}
          </ul>
        </>
      )}
      {state === "timeout" && (<><Clock className="mx-auto text-hp-gold" size={48} /><h1 className="font-display font-black uppercase tracking-tight text-3xl mt-6">Paiement en cours de confirmation</h1><p className="text-neutral-400 mt-3">Vous recevrez un e-mail dès validation. Si rien n'arrive, contactez votre centre.</p></>)}
      {state === "error" && (<><XCircle className="mx-auto text-red-400" size={48} /><h1 className="font-display font-black uppercase tracking-tight text-3xl mt-6">Paiement non abouti</h1><p className="text-neutral-400 mt-3">Votre panier est conservé, vous pouvez réessayer.</p></>)}
      <Link to="/boutique" className="hp-ghost inline-flex px-8 py-3.5 mt-10 text-sm" data-testid="back-shop">Retour à la boutique</Link>
    </div>
  );
}
